import React, { useCallback } from "react";
import { gql, useQuery, useSubscription } from "@apollo/client";
import {
  Transaction,
  // TransactionPage,
  FindTransactionsByCommunityIdResponse,
  QueryFindTransactionsByCommunityIdArgs
} from "../../../../schemaTypes";
import { newTransactionSubscription } from "../FindTransactions";

// GraphQL query for transactions by communityId
export const findTransactionsByCommunityIdQuery = gql`
  query FindTransactionsByCommunityId(
    $communityId: String!
    $page: Int!
    $pageSize: Int!
    $statusFilter: String
    $typeFilter: String
    $searchText: String
    $sortBy: String
    $sortOrder: String
  ) {
    findTransactionsByCommunityId(
      communityId: $communityId
      page: $page
      pageSize: $pageSize
      statusFilter: $statusFilter
      typeFilter: $typeFilter
      searchText: $searchText
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      data {
        transaction {
          id
          amount
          charge
          reference
          type
          status
          currency
          transferTypes
          # wallet { 
          #   id 
          #   balance 
          #   owner { 
          #     id 
          #     customer { 
          #       firstname 
          #       lastname 
          #     } 
          #   } 
          #   createdAt 
          #   updatedAt 
          # }
          # recipientWallet { 
          #   id 
          #   balance 
          #   currency 
          #   owner { 
          #     admin { 
          #       merchantProfile { 
          #         companyName 
          #       } 
          #     } 
          #   } 
          #   createdAt 
          #   updatedAt 
          # }
          bankPreference { 
            id 
          }
          offer { 
            id 
            title 
            description 
            category { 
              name 
            } 
            createdBy{
              id
              role
              # admin{
              #   id
              #   merchantProfile{
              #     id
              #     companyName
              #     businessName
              #   }
              # }
            }
          }
          njoyCode
          partnerVoucher
          user { 
            id  
            customer{
              id
              firstname
              lastname
            } 
            # createdAt 
            # updatedAt
          }
          createdAt
          updatedAt
        }
      }
      redeemedOffers
      totalSpend
      totalCustomers
      totalCommunitySize
      total
      page
      pageSize
      totalPages
    }
  }
`;

export interface WithFindTransactionsByCommunityId {
  transactions: Transaction[];
  loading: boolean;
  loadMore: () => void;
  hasMoreTransactions: boolean;
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  redeemedOffers: number
  totalSpend: number,
  totalCustomers: number
  totalCommunitySize: number
}

interface Props {
  variables: QueryFindTransactionsByCommunityIdArgs;
  children: (data: WithFindTransactionsByCommunityId) => React.ReactElement | null;
}

export const FindTransactionsByCommunityId: React.FC<Props> = ({ children, variables }) => {
  const { data, loading, fetchMore } = useQuery<
    // { findTransactionsByCommunityId: TransactionPage },
    { findTransactionsByCommunityId: FindTransactionsByCommunityIdResponse },
    QueryFindTransactionsByCommunityIdArgs
  >(findTransactionsByCommunityIdQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  useSubscription(newTransactionSubscription);

  const transactions = data?.findTransactionsByCommunityId?.data || [];
  const page = data?.findTransactionsByCommunityId?.page || variables.page;
  const pageSize = data?.findTransactionsByCommunityId?.pageSize || variables.pageSize;
  const total = data?.findTransactionsByCommunityId?.total || 0;
  const totalPages = data?.findTransactionsByCommunityId?.totalPages || 0;
  const hasMoreTransactions = page < totalPages;
  const redeemedOffers = data?.findTransactionsByCommunityId?.redeemedOffers || 0;
  const totalSpend = data?.findTransactionsByCommunityId?.totalSpend || 0;
  const totalCustomers = data?.findTransactionsByCommunityId?.totalCustomers || 0;
  const totalCommunitySize = data?.findTransactionsByCommunityId?.totalCommunitySize || 0;

  const loadMore = useCallback(() => {
    if (!hasMoreTransactions) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.findTransactionsByCommunityId) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          findTransactionsByCommunityId: {
            ...fetchMoreResult.findTransactionsByCommunityId,
            data: [
              ...(prev?.findTransactionsByCommunityId?.data || []),
              ...(fetchMoreResult.findTransactionsByCommunityId.data || [])
            ]
          }
        };
      }
    });
  }, [fetchMore, variables, page, hasMoreTransactions]);

  return children({
    transactions: transactions as any[],
    loading,
    loadMore,
    hasMoreTransactions,
    page,
    pageSize,
    total,
    totalPages,
    redeemedOffers,
    totalSpend,
    totalCustomers,
    totalCommunitySize
  });
};